export class CircularReferenceDetector {
  private dependencyGraph: Map<string, string[]> = new Map();

  public async checkCircularReference(outputRange: string, inputRanges: string[]): Promise<boolean> {
    const normalizedOutput = this.normalizeRangeAddress(outputRange);
    const normalizedInputs = inputRanges.map((range) => this.normalizeRangeAddress(range));

    // Create a temporary graph with the potential new dependency
    const tempGraph = new Map(this.dependencyGraph);
    tempGraph.set(normalizedOutput, normalizedInputs);

    // Check for circular references
    return this.detectCircularPath(tempGraph, normalizedOutput);
  }

  public addDependency(outputRange: string, inputRanges: string[]): void {
    const normalizedOutput = this.normalizeRangeAddress(outputRange);
    const normalizedInputs = inputRanges.map((range) => this.normalizeRangeAddress(range));
    this.dependencyGraph.set(normalizedOutput, normalizedInputs);
  }

  public clearDependencies(): void {
    this.dependencyGraph.clear();
  }

  public removeDependency(outputRange: string): void {
    const normalizedOutput = this.normalizeRangeAddress(outputRange);
    this.dependencyGraph.delete(normalizedOutput);
  }

  private async detectCircularPath(graph: Map<string, string[]>, startNode: string): Promise<boolean> {
    const visited = new Set<string>();
    const currentPath = new Set<string>();
    return this.depthFirstSearch(graph, startNode, visited, currentPath);
  }

  private async depthFirstSearch(
    graph: Map<string, string[]>,
    node: string,
    visited: Set<string>,
    currentPath: Set<string>
  ): Promise<boolean> {
    // Immediate cycle detection
    if (currentPath.has(node)) {
      const pathArray = Array.from(currentPath);
      const cycleStart = pathArray.indexOf(node);
      const cycle = [...pathArray.slice(cycleStart), node];
      console.warn(`Circular reference detected: ${cycle.join(" → ")}`);
      return true;
    }

    // Skip already verified paths
    if (visited.has(node)) {
      return false;
    }

    // Add current node to path and visited set
    currentPath.add(node);
    visited.add(node);

    // Get dependencies (inputRanges) for current node
    const dependencies = graph.get(node) || [];

    // Check each dependency
    for (const dependency of dependencies) {
      // For each dependency, check for range overlaps with any output ranges in the graph
      for (const [outputRange, _] of graph.entries()) {
        // If this dependency overlaps with another formula's output, check if that creates a path back to starting node
        if (await this.checkRangeOverlap(dependency, outputRange)) {
          if (await this.depthFirstSearch(graph, outputRange, visited, currentPath)) {
            return true;
          }
        }
      }
    }

    // Remove from current path after checking all dependencies
    currentPath.delete(node);
    return false;
  }

  private async checkRangeOverlap(range1: string, range2: string): Promise<boolean> {
    return await Excel.run(async (context) => {
      try {
        const sheet1 = this.getSheetName(range1);
        const sheet2 = this.getSheetName(range2);
        // Different sheets can't overlap
        if (sheet1.toLowerCase() !== sheet2.toLowerCase()) {
          return false;
        }
        const worksheet = context.workbook.worksheets.getItem(sheet1);
        const excelRange1 = worksheet.getRange(this.getRangeAddress(range1));
        const excelRange2 = worksheet.getRange(this.getRangeAddress(range2));
        const intersectionRange = excelRange1.getIntersectionOrNullObject(excelRange2);
        await context.sync();
        return !intersectionRange.isNullObject;
      } catch (error) {
        console.error("Error checking range overlap:", error);
        return true; // Assume overlap if error occurs (safer)
      }
    });
  }

  private getSheetName(range: string): string {
    if (!range.includes("!")) {
      throw new Error("Invalid range format. Expected 'SheetName!Address'.");
    }
    const parts = range.split("!");
    return parts[0].replace(/'/g, "").replace(/"/g, "");
  }

  private getRangeAddress(range: string): string {
    if (!range.includes("!")) {
      throw new Error("Invalid range format. Expected 'SheetName!Address'.");
    }
    const parts = range.split("!");
    return parts[1];
  }

  private normalizeRangeAddress(range: string): string {
    const sheetName = this.getSheetName(range).toLowerCase();
    const address = this.getRangeAddress(range).toUpperCase();
    return `${sheetName}!${address}`;
  }
}
